/**
 * Charge types — per-society bill charge catalog CRUD.
 *
 * Phase 1 of the Charges module. The society admin curates the list of
 * charge types that bills can be broken down into (Maintenance / Parking /
 * Sinking fund / Repair / Festival / Water / …). Each bill consumes this
 * catalog via line items that reference the charge type by code + carry a
 * denormalised label.
 *
 * Code is a lowercase slug (immutable post-create) — historical bills
 * reference it via FK and label-snapshot, so renaming the catalog label
 * does NOT mutate older bills.
 *
 * Delete is soft-only. The API refuses with `CHARGE_TYPE_IN_USE` if any
 * bill line item still references the charge type — admin should deactivate
 * via the Active toggle instead.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit3, Trash2, Tags, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';

interface ChargeTypeRow {
  id: string;
  code: string;
  label: string;
  description: string | null;
  default_amount_inr: number;
  sort_order: number;
  active: boolean;
  gst_pct: number | null;
  hsn_sac_code: string | null;
  created_at: string;
  updated_at: string;
}

interface ListResponse {
  items: ChargeTypeRow[];
  next_cursor?: string | null;
}

const CHARGE_TYPES_PAGE_SIZE = 50;

interface RateRow {
  id: string;
  charge_type_id: string;
  unit_type: string;
  amount_inr: number;
}

interface RateListResponse {
  items: RateRow[];
}

// Default unit-type slugs covered by the rate matrix. Admins can still POST
// arbitrary slugs (e.g. "shop", "penthouse") via API; this list controls
// which columns the matrix UI renders by default.
const DEFAULT_UNIT_TYPES = ['1bhk', '2bhk', '3bhk', '4bhk_plus', 'other'] as const;
const UNIT_TYPE_LABEL: Record<string, string> = {
  '1bhk': '1 BHK',
  '2bhk': '2 BHK',
  '3bhk': '3 BHK',
  '4bhk_plus': '4 BHK+',
  other: 'Other',
  shop: 'Shop',
  parking: 'Parking',
  common_area: 'Common area'
};

const CreateSchema = z.object({
  code: z
    .string()
    .trim()
    .min(2, 'Code must be ≥2 chars')
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/, 'lowercase letters / digits / underscore (e.g. "gym")'),
  label: z.string().trim().min(2, 'Label is required').max(80),
  description: z.string().trim().max(300).optional().or(z.literal('')),
  default_amount_inr: z.coerce.number().int().min(0).max(1_000_000),
  sort_order: z.coerce.number().int().min(0).max(10000),
  active: z.boolean(),
  // Phase 3 — optional GST % (free-form 0-50, standard slabs are 5/12/18/28)
  // and HSN/SAC code printed on the receipt PDF.
  gst_pct: z.coerce.number().min(0).max(50).optional().or(z.literal('').transform(() => undefined)),
  hsn_sac_code: z
    .string()
    .trim()
    .max(10)
    .regex(/^[0-9]*$/, 'HSN/SAC must be digits only')
    .optional()
    .or(z.literal(''))
});
type CreateForm = z.infer<typeof CreateSchema>;

const DEFAULT_FORM: CreateForm = {
  code: '',
  label: '',
  description: '',
  default_amount_inr: 0,
  sort_order: 100,
  active: true,
  gst_pct: undefined,
  hsn_sac_code: ''
};

function formatInr(rupees: number): string {
  return `₹${rupees.toLocaleString('en-IN')}`;
}

export function ChargeTypesPage(): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ChargeTypeRow | null>(null);
  const [confirmDeleteFor, setConfirmDeleteFor] = useState<ChargeTypeRow | null>(null);

  const form = useForm<CreateForm>({
    resolver: zodResolver(CreateSchema),
    defaultValues: DEFAULT_FORM,
    mode: 'onChange'
  });

  // Cursor pagination — no client-side filters yet, so the hash is static.
  // If filters are added later, fold them into this hash so accumulation
  // resets on filter change.
  const filtersHash = 'static';
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const listQuery = useQuery({
    queryKey: ['charge-types', cursor],
    queryFn: () =>
      get<ListResponse>('/charge-types', {
        params: { limit: CHARGE_TYPES_PAGE_SIZE, ...(cursor ? { cursor } : {}) }
      }),
    placeholderData: (prev) => prev
  });
  const paginator = useCursorAccumulator<ChargeTypeRow>(listQuery.data, cursor, filtersHash);

  const items = paginator.items;

  // Phase 2: per-unit-type rate overrides. Fetched separately so the matrix
  // re-renders independently from the catalog edits above.
  const ratesQuery = useQuery({
    queryKey: ['charge-type-rates'],
    queryFn: () => get<RateListResponse>('/charge-type-rates')
  });
  const rates = ratesQuery.data?.items ?? [];
  const rateMap = new Map<string, RateRow>();
  for (const r of rates) rateMap.set(`${r.charge_type_id}|${r.unit_type}`, r);

  const upsertRate = useMutation({
    mutationFn: (vars: { charge_type_id: string; unit_type: string; amount_inr: number }) =>
      post<RateRow>('/charge-type-rates', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['charge-type-rates'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const deleteRate = useMutation({
    mutationFn: (id: string) => del<void>(`/charge-type-rates/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['charge-type-rates'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const onRateBlur = (
    chargeTypeId: string,
    unitType: string,
    newValue: string,
    existing: RateRow | undefined
  ): void => {
    const v = newValue.trim();
    // Empty string = remove the override (fall back to default_amount_inr).
    if (v === '' || v === '0') {
      if (existing) deleteRate.mutate(existing.id);
      return;
    }
    const n = parseInt(v, 10);
    if (!Number.isFinite(n) || n < 0) {
      toast.error('Amount must be a non-negative integer');
      return;
    }
    if (existing && existing.amount_inr === n) return; // no change
    upsertRate.mutate({ charge_type_id: chargeTypeId, unit_type: unitType, amount_inr: n });
  };

  const openCreate = (): void => {
    setEditing(null);
    form.reset(DEFAULT_FORM);
    setOpen(true);
  };
  const openEdit = (k: ChargeTypeRow): void => {
    setEditing(k);
    form.reset({
      code: k.code,
      label: k.label,
      description: k.description ?? '',
      default_amount_inr: k.default_amount_inr,
      sort_order: k.sort_order,
      active: k.active,
      gst_pct: k.gst_pct ?? undefined,
      hsn_sac_code: k.hsn_sac_code ?? ''
    });
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: async (v: CreateForm) => {
      // Strip empty description → omit from payload (server treats as null).
      const description = v.description?.trim() ? v.description.trim() : undefined;
      if (editing) {
        // code is immutable post-create
        const { code: _drop, ...rest } = v;
        return patch<ChargeTypeRow>(`/charge-types/${editing.id}`, { ...rest, description: description ?? null });
      }
      return post<ChargeTypeRow>('/charge-types', { ...v, description });
    },
    onSuccess: () => {
      toast.success(editing ? 'Charge type updated' : 'Charge type added');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['charge-types'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<{ ok: true }>(`/charge-types/${id}`),
    onSuccess: () => {
      toast.success('Charge type deleted');
      setConfirmDeleteFor(null);
      void qc.invalidateQueries({ queryKey: ['charge-types'] });
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
      setConfirmDeleteFor(null);
    }
  });

  const activeCount = items.filter((k) => k.active).length;
  const totalDefault = items.filter((k) => k.active).reduce((s, k) => s + k.default_amount_inr, 0);

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title="Charge types"
        description="Catalog of bill charges (Maintenance, Parking, Sinking fund, …). Bills break down into line items that reference these codes."
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" /> Add charge type
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Total charge types</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">{items.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Active</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">{activeCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Default total / bill</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">{formatInr(totalDefault)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4 flex items-center gap-vt-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
              <Tags className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <div>
              <div className="text-vt-12 text-vt-text-muted">Phase</div>
              <div className="text-vt-14 font-semibold text-vt-text">1 of 3</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-vt-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Wallet className="h-6 w-6" strokeWidth={1.75} />}
              title="No charge types yet"
              description="Add the charge types your society bills against."
              action={
                <Button onClick={openCreate}>
                  <Plus className="h-4 w-4" /> Add charge type
                </Button>
              }
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead className="text-right">Default amount</TableHead>
                    <TableHead className="text-right">Sort</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((k) => (
                    <TableRow key={k.id}>
                      <TableCell className="font-mono text-vt-12">{k.code}</TableCell>
                      <TableCell>
                        <div className="font-medium text-vt-text">{k.label}</div>
                        {k.description ? (
                          <div className="line-clamp-1 text-vt-12 text-vt-text-muted">{k.description}</div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                        {formatInr(k.default_amount_inr)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-vt-12 text-vt-text-muted">
                        {k.sort_order}
                      </TableCell>
                      <TableCell>
                        <Badge variant={k.active ? 'ok' : 'muted'}>
                          {k.active ? 'Active' : 'Disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-vt-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(k)} title="Edit">
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmDeleteFor(k)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="px-vt-4 pb-vt-3">
                <CursorPaginator
                  hasMore={Boolean(paginator.nextCursor)}
                  loading={listQuery.isFetching}
                  itemsShown={items.length}
                  pageSize={CHARGE_TYPES_PAGE_SIZE}
                  onLoadMore={() => {
                    if (paginator.nextCursor) setCursor(paginator.nextCursor);
                  }}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Phase 2: per-unit-type rate matrix */}
      <Card>
        <CardContent className="p-vt-4">
          <div className="mb-vt-2 flex items-center justify-between">
            <div>
              <div className="text-vt-14 font-semibold text-vt-text">Per-unit-type rates</div>
              <div className="text-vt-12 text-vt-text-muted">
                Override the default amount per flat size. Empty cells fall back to the charge
                type's default. Saves on blur.
              </div>
            </div>
          </div>
          {items.length === 0 ? (
            <p className="text-vt-12 text-vt-text-muted">Add at least one charge type above first.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-vt-13">
                <thead className="bg-vt-surface-2">
                  <tr>
                    <th className="px-vt-3 py-vt-2 text-left text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                      Charge type
                    </th>
                    <th className="px-vt-2 py-vt-2 text-right text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                      Default
                    </th>
                    {DEFAULT_UNIT_TYPES.map((ut) => (
                      <th
                        key={ut}
                        className="px-vt-2 py-vt-2 text-right text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted"
                      >
                        {UNIT_TYPE_LABEL[ut] ?? ut}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items
                    .filter((ct) => ct.active)
                    .map((ct) => (
                      <tr key={ct.id} className="border-t border-vt-border">
                        <td className="px-vt-3 py-vt-2 align-middle">
                          <div className="font-medium text-vt-text">{ct.label}</div>
                          <div className="font-mono text-vt-11 text-vt-text-muted">{ct.code}</div>
                        </td>
                        <td className="px-vt-2 py-vt-2 text-right align-middle font-mono text-vt-12 text-vt-text-muted">
                          {formatInr(ct.default_amount_inr)}
                        </td>
                        {DEFAULT_UNIT_TYPES.map((ut) => {
                          const existing = rateMap.get(`${ct.id}|${ut}`);
                          return (
                            <td key={ut} className="px-vt-2 py-vt-2 align-middle">
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                placeholder={ct.default_amount_inr.toString()}
                                defaultValue={existing?.amount_inr ?? ''}
                                onBlur={(e) => onRateBlur(ct.id, ut, e.target.value, existing)}
                                className="h-8 text-right font-mono text-vt-12"
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : setOpen(false))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit charge type' : 'Add charge type'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Update label, default amount, sort and active toggle. Code is immutable so historical bills stay stable.'
                : 'Pick a short lowercase code (e.g. gym, cable, security). It cannot be changed later.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="flex flex-col gap-vt-3">
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="ct-code">Code</Label>
                <Input
                  id="ct-code"
                  placeholder="gym"
                  disabled={Boolean(editing)}
                  {...form.register('code')}
                />
                {form.formState.errors.code ? (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.code.message}</p>
                ) : null}
                {editing ? (
                  <p className="text-vt-11 text-vt-text-muted">
                    Code is immutable — existing bill line items reference it.
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="ct-label">Label</Label>
                <Input id="ct-label" placeholder="Gym" {...form.register('label')} />
                {form.formState.errors.label ? (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.label.message}</p>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="ct-description">Description (optional)</Label>
              <Textarea
                id="ct-description"
                rows={2}
                placeholder="Per-month gym + clubhouse access charge"
                {...form.register('description')}
              />
              {form.formState.errors.description ? (
                <p className="text-vt-12 text-vt-alert">{form.formState.errors.description.message}</p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="ct-amount">Default amount (₹)</Label>
                <Input
                  id="ct-amount"
                  type="number"
                  min={0}
                  max={1_000_000}
                  step={1}
                  {...form.register('default_amount_inr')}
                />
                <p className="text-vt-11 text-vt-text-muted">
                  Suggested per-month amount. The bill-create form can override per-bill.
                </p>
                {form.formState.errors.default_amount_inr ? (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.default_amount_inr.message}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="ct-sort">Sort order</Label>
                <Input
                  id="ct-sort"
                  type="number"
                  min={0}
                  max={10000}
                  {...form.register('sort_order')}
                />
                <p className="text-vt-11 text-vt-text-muted">
                  Lower = higher in pickers. Defaults: 10/20/30 for seeded types.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="ct-gst">GST % (optional)</Label>
                <Input
                  id="ct-gst"
                  type="number"
                  min={0}
                  max={50}
                  step={0.01}
                  placeholder="e.g. 18"
                  {...form.register('gst_pct')}
                />
                <p className="text-vt-11 text-vt-text-muted">
                  Standard slabs: 0 / 5 / 12 / 18 / 28. Leave blank for no GST.
                </p>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="ct-hsn">HSN / SAC code (optional)</Label>
                <Input
                  id="ct-hsn"
                  type="text"
                  maxLength={10}
                  placeholder="e.g. 9954"
                  {...form.register('hsn_sac_code')}
                />
                <p className="text-vt-11 text-vt-text-muted">
                  Printed on the receipt PDF. 4-10 digits.
                </p>
              </div>
            </div>

            <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
              <input
                type="checkbox"
                {...form.register('active')}
                className="h-4 w-4 accent-vt-primary"
              />
              Active (visible in new-bill pickers)
            </label>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={save.isPending}
                loadingText="Saving…"
                disabled={save.isPending || !form.formState.isValid}
              >
                {editing ? 'Save changes' : 'Add charge type'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={Boolean(confirmDeleteFor)}
        onOpenChange={(v) => (v ? null : setConfirmDeleteFor(null))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete charge type?</DialogTitle>
            <DialogDescription>
              {confirmDeleteFor ? (
                <>
                  Delete <span className="font-semibold">{confirmDeleteFor.label}</span> ({confirmDeleteFor.code})?
                  If any bill line item still references this code, the API will reject the delete with
                  CHARGE_TYPE_IN_USE — deactivate via the Active toggle instead.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setConfirmDeleteFor(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              loadingText="Deleting…"
              onClick={() => confirmDeleteFor && remove.mutate(confirmDeleteFor.id)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
